// Shared Chart.js setup for the chart custom elements. Chart.js v4 is
// tree-shakable: only the pieces the line + donut charts actually use are
// registered — no built-in legend (legends here are server-rendered HTML).
//
// Theme values resolve from the CSS custom properties in application.css, so
// the palette stays single-sourced. Because the app's theme switches at
// runtime (settings → Appearance), a MutationObserver on <html data-theme>
// re-applies defaults and tells every chart element to rebuild.

import {
	ArcElement,
	CategoryScale,
	Chart,
	DoughnutController,
	Filler,
	LinearScale,
	LineController,
	LineElement,
	PointElement,
	Tooltip,
} from "chart.js";

let registered = false;

export function ensureChartTheme(): void {
	if (!registered) {
		registered = true;

		Chart.register(
			LineController,
			LineElement,
			PointElement,
			Filler,
			CategoryScale,
			LinearScale,
			DoughnutController,
			ArcElement,
			Tooltip,
		);

		watchThemeAttribute();
	}

	applyTheme();
}

function applyTheme(): void {
	Chart.defaults.font.family = cssValue("--font-sans") || "sans-serif";
	Chart.defaults.font.size = 11;
	Chart.defaults.color = chartColor("--color-ink-faint");
	Chart.defaults.animation = false;

	// Tooltip chrome matches the app's panel surfaces — in either theme.
	const tooltip = Chart.defaults.plugins.tooltip;
	tooltip.backgroundColor = chartColor("--color-panel");
	tooltip.borderColor = chartColor("--color-edge");
	tooltip.borderWidth = 1;
	tooltip.titleColor = chartColor("--color-ink");
	tooltip.bodyColor = chartColor("--color-ink-muted");
	tooltip.padding = 10;
	tooltip.cornerRadius = 10;
	tooltip.caretSize = 5;
	tooltip.usePointStyle = true;
	tooltip.boxWidth = 7;
	tooltip.boxHeight = 7;
	tooltip.boxPadding = 4;
}

// Rebuild-on-theme-switch: elements subscribe with their abort signal; the
// observer re-applies defaults first so rebuilds pick up the new palette.
const THEME_EVENT = "chart-theme:changed";
let watching = false;

function watchThemeAttribute(): void {
	if (watching) return;
	watching = true;

	new MutationObserver(() => {
		applyTheme();
		window.dispatchEvent(new CustomEvent(THEME_EVENT));
	}).observe(document.documentElement, {
		attributes: true,
		attributeFilter: ["data-theme"],
	});
}

export function onThemeChange(signal: AbortSignal, rebuild: () => void): void {
	window.addEventListener(THEME_EVENT, rebuild, { signal });
}

// Payload colours are CSS custom-property names ("--color-brand-500") so the
// server only ever speaks in palette tokens; anything not var-shaped passes
// through as a literal for one-off values.
export function chartColor(token: string): string {
	if (!token.startsWith("--")) return token;
	return cssValue(token) || "#9d9da7";
}

// "#rrggbb" -> rgba() at the given alpha, for translucent takes on palette
// colours (the line chart's area fill). Non-hex values pass through.
export function withAlpha(color: string, alpha: number): string {
	const match = /^#([0-9a-f]{6})$/i.exec(color.trim());
	if (!match) return color;
	const int = Number.parseInt(match[1], 16);
	return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, ${alpha})`;
}

function cssValue(name: string): string {
	return getComputedStyle(document.documentElement)
		.getPropertyValue(name)
		.trim();
}

// All chart money is integer pence, same contract as the server. Whole and
// compact for chrome (axes, centre labels); exact for tooltips, where the
// figure is the point of hovering.
export function gbp(
	pence: number,
	style: "exact" | "whole" | "compact" = "exact",
): string {
	return new Intl.NumberFormat("en-GB", {
		style: "currency",
		currency: "GBP",
		notation: style === "compact" ? "compact" : "standard",
		minimumFractionDigits: style === "exact" ? 2 : 0,
		maximumFractionDigits: style === "exact" ? 2 : style === "compact" ? 1 : 0,
	}).format(pence / 100);
}

// The server renders chart data as a JSON script child — data in markup,
// behaviour in the element. A missing script/canvas is a markup bug worth a
// loud error.
export function readPayload<T>(host: HTMLElement, tag: string): T {
	const script = host.querySelector<HTMLScriptElement>(
		'script[type="application/json"][data-chart-data]',
	);
	if (!script) throw new Error(`<${tag}>: missing data-chart-data script`);
	return JSON.parse(script.textContent || "null") as T;
}

export function findCanvas(host: HTMLElement, tag: string): HTMLCanvasElement {
	const canvas = host.querySelector("canvas");
	if (!canvas) throw new Error(`<${tag}>: missing canvas`);
	return canvas;
}
